import React, { useMemo } from 'react'
import Head from 'next/head'
import type {
  GetStaticProps,
  NextPage,
  InferGetStaticPropsType,
  GetStaticPaths,
} from 'next'

import useAugmentedRouter from 'hooks/useAugmentedRouter'

import Loader from 'components/Loader/Loader'
import Header from 'components/Header/Header'
import Footer from 'components/Footer/Footer'
import Gtm from 'components/scripts/Gtm'

import style from './RentalAgreement.module.scss'

import { BASE_WP_API_URL } from 'config/cms'

import { parseDataString } from 'utils/Strings'
import {
  fetchFooterData,
  fetchGlobalOptionsData,
  fetchWordpressPageData,
  fetchListingPageData,
} from 'utils/staticData'
import type { CombinedListing } from 'utils/staticData'

const title = 'Rental Agreement'

type RentalAgreementProps = {
  footerData: any
  pageData: any
  listing: CombinedListing | null
  lastModifiedDate: string
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      {
        params: {
          listingId: undefined,
        },
      },
    ],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<RentalAgreementProps> = async (
  context,
) => {
  const pageData = await fetchWordpressPageData('rental-agreement')
  const footerData = await fetchFooterData()
  const globalOptionsData = await fetchGlobalOptionsData()

  const listing = context.params?.listingId
    ? await fetchListingPageData(context.params.listingId as string).catch(
        () => null,
      )
    : null

  return {
    props: {
      pageData: pageData,
      footerData: {
        menus: footerData,
        options: globalOptionsData.acf.global_footer_group,
      },
      listing: listing,
      lastModifiedDate: new Date().toString(),
    },
    revalidate: process.env.DEFAULT_REVALIDATION_PERIOD
      ? parseInt(process.env.DEFAULT_REVALIDATION_PERIOD)
      : undefined,
  }
}

const RentalAgreement: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ footerData, pageData, listing }) => {
  const router = useAugmentedRouter()
  const houseRules = useMemo(() => {
    const rules = listing?.['adContent']?.filter(
      ({ type }) => type === 'additionToRentalAgreement',
    )

    if (rules && rules.length && 'text' in rules[0]) {
      const parsedRules = parseDataString(rules[0]['text'])
      return parsedRules
    } else return null
  }, [listing])

  const renderHouseRules = (str) => {
    const content: JSX.Element[] = []

    if (str && str.length) {
      for (let i = 0; i < str.length; i++) {
        if (str[i] !== 'null') {
          content.push(
            <div key={i}>
              <p>{str[i]}</p>
            </div>,
          )
        }
      }
    }

    return content
  }

  return router.isFallback ? (
    <Loader />
  ) : (
    <>
      <Header />
      <div className={style.container}>
        <Head>
          <title>{title}</title>

          <link
            href={`${BASE_WP_API_URL}/wp-content/uploads/elementor/css/post-${pageData.id}.css`}
            rel="stylesheet"
          />
        </Head>
        <Gtm pageType="rental agreement page" />
        <div
          dangerouslySetInnerHTML={{
            __html: pageData.content.rendered,
          }}
        />
        {houseRules && houseRules.length ? (
          <div className={style.houseRules}>{renderHouseRules(houseRules)}</div>
        ) : null}
      </div>
      <Footer footerData={footerData} />
    </>
  )
}

export default RentalAgreement
